import moment from 'moment-timezone';

function assertValidDateRange(startDate: string, endDate: string) {
  if (!startDate) throw new Error('startDate cannot be null or undefined');
  if (!endDate) throw new Error('endDate cannot be null or undefined');
  if (!moment(startDate).isValid()) throw new Error(`Invalid startDate: "${startDate}"`);
  if (!moment(endDate).isValid()) throw new Error(`Invalid endDate: "${endDate}"`);
}

function reorderChronologically(startDate: string, endDate: string) {
  if (moment(startDate).isAfter(moment(endDate))) {
    return { start: endDate, end: startDate };
  }
  return { start: startDate, end: endDate };
}

// business rules: docs/flow-insights-and-run-logs-date-range-rules.md#business-rules
function expandSameDaySelection(start: string, end: string, userTimeZone: string) {
  if (start !== end) return null;

  const startOfLocalDay = moment.tz(start, userTimeZone).startOf('day');
  return {
    start: startOfLocalDay.toISOString(),
    end: startOfLocalDay.clone().add(1, 'day').toISOString(),
  };
}

function isStaticRangeEnd(endMoment: moment.Moment) {
  return endMoment.isSame(endMoment.clone().endOf('day'));
}

// react-date-range never applies endOfDay to `end`.
// See docs/flow-insights-and-run-logs-date-range-rules.md#business-rules
function isCustomMultiDaySelection(startMoment: moment.Moment, endMoment: moment.Moment) {
  return (
    startMoment.isSame(startMoment.clone().startOf('day')) &&
    endMoment.isSame(endMoment.clone().startOf('day'))
  );
}

// full rules and scenarios: docs/flow-insights-and-run-logs-date-range-rules.md
function applyExclusiveUpperBound(start: string, end: string, userTimeZone: string) {
  const startMoment = moment.tz(start, userTimeZone);
  const endMoment = moment.tz(end, userTimeZone);

  if (isStaticRangeEnd(endMoment)) {
    return endMoment.add(1, 'millisecond').toISOString();
  }

  if (isCustomMultiDaySelection(startMoment, endMoment)) {
    return endMoment.add(1, 'day').toISOString();
  }

  return end;
}

export function normalizeDateRange(
  startDate: string,
  endDate: string,
  userTimeZone: string = 'UTC',
) {
  assertValidDateRange(startDate, endDate);

  const { start, end } = reorderChronologically(startDate, endDate);

  const sameDayRange = expandSameDaySelection(start, end, userTimeZone);
  if (sameDayRange) return sameDayRange;

  // endpoint precision rules:
  // docs/flow-insights-and-run-logs-date-range-rules.md#best-practices-when-touching-this-code
  return { start, end: applyExclusiveUpperBound(start, end, userTimeZone) };
}
